/**
 * `applyResolved` — **rules-free** playback of a resolved step list.
 *
 * This is the playback half of draw-and-record. The authority's `resolve` (in
 * `@lancer-vtt/rules`) runs the rules **once**, drawing dice and recording each
 * outcome into a {@link ResolvedStep} list. *Everyone else* — every other client,
 * log replay, and redo — folds that list onto state through `applyResolved`,
 * which contains **no rules**: it never rolls a die, never reads evasion, never
 * decides hit vs miss. It just applies the recorded grounded deltas. That is what
 * makes a recorded fight **version-stable** (replaying it reproduces the historical
 * outcome even after a later formula patch — re-running rules over old dice would
 * silently rewrite it) and **order-stable** (order is recorded, not re-derived).
 *
 * It lives in `@lancer-vtt/domain` next to the reducer (not in `@lancer-vtt/rules`)
 * precisely because it is rules-free: the reducer and the history both run it, and
 * `domain` must never depend on `rules`. `resolve` (rules) produces the steps;
 * `applyResolved` (rules-free) folds them.
 *
 * Record-only steps (`to_hit`, `damage`) mutate nothing here — they are the dice
 * witness for the play-by-play. Only grounded steps (`hp_delta`) change state.
 */

import type { AffordanceStateEntry } from "./model/affordance";
import type { CombatantCurrent } from "./model/combatant";
import type { EncounterState } from "./types";
import { destroyedItemResourceKey, makeStatusInstance, withDisclosureLevel, withRevealedFeatures } from "./types";
import type { ResolvedStep } from "./steps";

/** Clamp `n` into `[lo, hi]`. */
function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Write one field of one entry in {@link CombatantCurrent.affordanceState} — the per-affordance
 * state ledger (`decisions/0176`) — honoring the **sparse-absent** convention end to end: an
 * entry with no live field is dropped, and a ledger with no live entry goes back to `undefined`
 * rather than leaving an empty-object residue. That is what lets a spend-then-undo restore
 * genuine absence, so a fresh run and an undone run are byte-identical, not merely equivalent
 * (the same discipline `resource_clear`/`prevAbsent` enforce for `resources`).
 *
 * Shared by the `discharge_set` mirror and the `affordance_use_set` step so the two can't drift
 * on the drop rule.
 */
function withAffordanceEntry(
  current: CombatantCurrent,
  key: string,
  patch: (entry: AffordanceStateEntry) => AffordanceStateEntry,
): CombatantCurrent {
  const table = current.affordanceState ?? {};
  const nextEntry = patch(table[key] ?? {});
  const nextTable: Record<string, AffordanceStateEntry> = { ...table };
  if (Object.keys(nextEntry).length === 0) delete nextTable[key];
  else nextTable[key] = nextEntry;
  if (Object.keys(nextTable).length === 0) {
    const next = { ...current };
    delete next.affordanceState;
    return next;
  }
  return { ...current, affordanceState: nextTable };
}

/** Set/clear one affordance's `discharged` flag (the `discharge_set` mirror). */
function withAffordanceFlag(current: CombatantCurrent, key: string, discharged: boolean): CombatantCurrent {
  return withAffordanceEntry(current, key, (entry) => {
    const next = { ...entry };
    if (discharged) next.discharged = true;
    else delete next.discharged;
    return next;
  });
}

/** Set (or, for `uses === undefined`, clear) one affordance's consumed-use count. */
function withAffordanceUses(current: CombatantCurrent, key: string, uses: number | undefined): CombatantCurrent {
  return withAffordanceEntry(current, key, (entry) => {
    const next = { ...entry };
    if (uses === undefined) delete next.uses;
    else next.uses = Math.max(0, uses);
    return next;
  });
}

/**
 * Fold one grounded step onto state, returning the new state. Record-only steps
 * return state unchanged. Pure — no dice, no rules.
 *
 * `hp_delta` clamps the result to `[0, maxHp]` (the same bound the manual `set_hp`
 * enforces). An unknown target id is ignored (a no-op rather than a throw): a
 * resolved step list is an already-validated record, and replay must be total —
 * it never re-validates and never rejects (the authority validated at `resolve`
 * time). A target removed between resolution and replay simply takes no damage.
 */
export function applyResolvedStep(state: EncounterState, step: ResolvedStep): EncounterState {
  switch (step.op) {
    case "hp_delta": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      const hp = clamp(c.current.hp + step.delta, 0, c.base.maxHp);
      if (hp === c.current.hp) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, current: { ...c.current, hp } },
        },
      };
    }
    case "heat_delta": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      // No heat track (non-mech kinds) → heat is inert; a recorded heat step is a
      // no-op rather than a throw (replay never re-validates). Emitters only ever put
      // heat onto a mech wielder, but stay total.
      if (c.base.maxHeat === undefined) return state;
      const curHeat = c.current.heat ?? 0;
      const heat = clamp(curHeat + step.delta, 0, c.base.maxHeat);
      if (heat === curHeat) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, current: { ...c.current, heat } },
        },
      };
    }
    case "structure_delta": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      // No structure track (pilots/objects) → inert no-op.
      if (c.base.maxStructure === undefined) return state;
      const cur = c.current.structure ?? c.base.maxStructure;
      const structure = clamp(cur + step.delta, 0, c.base.maxStructure);
      if (structure === cur) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, current: { ...c.current, structure } },
        },
      };
    }
    case "stress_delta": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      // No stress track (non-mech kinds) → inert no-op.
      if (c.base.maxStress === undefined) return state;
      const cur = c.current.stress ?? c.base.maxStress;
      const stress = clamp(cur + step.delta, 0, c.base.maxStress);
      if (stress === cur) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, current: { ...c.current, stress } },
        },
      };
    }
    case "destroy": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      const hasDestroyed = c.statuses.some((s) => s.name === "Destroyed");
      const hasVaporized = c.statuses.some((s) => s.name === "Vaporized");
      if (step.destroyed) {
        // Nothing new to add: already Destroyed, and either not vaporizing or already
        // carrying the Vaporized marker too.
        if (hasDestroyed && (!step.vaporized || hasVaporized)) return state;
        let statuses = hasDestroyed ? c.statuses : [...c.statuses, { name: "Destroyed", expiry: { kind: "scene" as const } }];
        // A vaporized destroy stamps a SECOND status beside `"Destroyed"` — every gate keyed
        // on the `"Destroyed"` name is untouched.
        if (step.vaporized && !hasVaporized) statuses = [...statuses, { name: "Vaporized", expiry: { kind: "scene" as const } }];
        return {
          ...state,
          combatants: { ...state.combatants, [step.target]: { ...c, statuses } },
        };
      }
      // The inverse: un-destroy always clears BOTH markers (a mech can't be "un-destroyed"
      // while still carrying the vaporized marker) — idempotent when neither is present.
      if (!hasDestroyed && !hasVaporized) return state;
      const statuses = c.statuses.filter((s) => s.name !== "Destroyed" && s.name !== "Vaporized");
      return {
        ...state,
        combatants: { ...state.combatants, [step.target]: { ...c, statuses } },
      };
    }
    case "destroy_item": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      // Folded into `current.resources`, mirroring the Loading/Limited convention (a namespaced
      // key in the same sparse bag), NOT a dedicated top-level field like `discharged` — see the
      // step's own doc. Presence (value 1) means destroyed; absence means intact.
      const resources = c.current.resources ?? {};
      const key = destroyedItemResourceKey(step.key);
      const has = (resources[key] ?? 0) >= 1;
      if (step.destroyed === has) return state; // idempotent.
      const next = { ...resources };
      if (step.destroyed) {
        next[key] = 1;
      } else {
        delete next[key];
      }
      // Drop `resources` back to absent when the last key clears (mirrors `resource_clear`'s
      // own "absent == pristine" discipline) rather than leaving an empty-object residue.
      const nextResources = Object.keys(next).length > 0 ? next : undefined;
      const current = { ...c.current, resources: nextResources };
      if (nextResources === undefined) delete current.resources;
      return {
        ...state,
        combatants: { ...state.combatants, [step.target]: { ...c, current } },
      };
    }
    case "discharge_set": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      // **One ledger, one guard** (`affordance-table` Phase 3.1). Through Phases 1-2 this case
      // wrote `current.discharged` AND mirrored into `current.affordanceState`, so the guard had
      // to require BOTH to agree before short-circuiting. `current.discharged` is deleted; the
      // per-affordance ledger is the only home for the flag, so idempotence is one read again.
      //
      // The guard, the two resolve-path emit-side guards (`resolveActivate`,
      // `foldWeaponFireBookkeeping`/`resolveAreaAttack`) and the pre-dice gates all read this
      // same table. That has to stay true: a guard on one substrate against a reducer that
      // short-circuits on another pushes a step the reducer no-ops, and that step's INVERSE then
      // clears a discharge that was already standing — an undo bug no forward-only test sees.
      const already = c.current.affordanceState?.[step.key]?.discharged === true;
      if (already === step.discharged) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, current: withAffordanceFlag(c.current, step.key, step.discharged) },
        },
      };
    }
    case "affordance_use_set": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, current: withAffordanceUses(c.current, step.key, step.uses) },
        },
      };
    }
    case "burn_set": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      const burn = Math.max(0, step.burn);
      if ((c.current.burn ?? 0) === burn) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, current: { ...c.current, burn } },
        },
      };
    }
    case "status_add": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      if (c.statuses.some((s) => s.name === step.status)) return state; // idempotent: already present (by name).
      // Every facet rides the step verbatim through the one builder, which owns the
      // `until_removed` default and the never-default rule for the rest (`appliedTurn`
      // above all — this case also replays an inverted `status_remove` on undo).
      // `StatusAddStep` has no `suspended` field, so none reaches the builder: that facet
      // round-trips on its own `status_suspend_set` rail (`steps.ts`'s `UncarriedStatusFacet`).
      const instance = makeStatusInstance({ ...step, name: step.status });
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, statuses: [...c.statuses, instance] },
        },
      };
    }
    case "status_remove": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      if (!c.statuses.some((s) => s.name === step.status)) return state; // idempotent: absent already (by name).
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, statuses: c.statuses.filter((s) => s.name !== step.status) },
        },
      };
    }
    case "status_suspend_set": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      const idx = c.statuses.findIndex((s) => s.name === step.status);
      if (idx === -1) return state; // no live instance of this name — a silent no-op.
      const inst = c.statuses[idx]!;
      if ((inst.suspended ?? false) === step.suspended) return state; // idempotent.
      const statuses = [...c.statuses];
      if (step.suspended) {
        statuses[idx] = { ...inst, suspended: true };
      } else {
        // Drop the key back to true absence (rather than an explicit `false` residue) —
        // the "absent means false" convention every other omitted-when-off StatusInstance
        // facet follows (`cause`/`partner`), so a fire-then-undo replay is byte-identical
        // to a state that never suspended this instance at all.
        const { suspended: _suspended, ...rest } = inst;
        statuses[idx] = rest;
      }
      return {
        ...state,
        combatants: { ...state.combatants, [step.target]: { ...c, statuses } },
      };
    }
    case "resource_set": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      const val = Math.max(0, step.val); // resources never go negative.
      const resources = c.current.resources ?? {};
      // Presence-aware no-op guard — NOT `(resources[step.key] ?? 0) === val`: a
      // resource whose absence means something other than 0 (Loading's `?? 1`)
      // needs writing an explicit 0 onto a currently-absent key to actually take
      // effect — collapsing "absent" and "present at 0" here would silently no-op a
      // Loading weapon's first-ever fire.
      if (Object.hasOwn(resources, step.key) && resources[step.key] === val) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: {
            ...c,
            current: { ...c.current, resources: { ...resources, [step.key]: val } },
          },
        },
      };
    }
    case "resource_clear": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      const resources = c.current.resources ?? {};
      if (!Object.hasOwn(resources, step.key)) return state; // already absent — idempotent.
      const next = { ...resources };
      delete next[step.key];
      // If that was the LAST key, drop `resources` itself back to absent (`undefined`) rather
      // than leaving an empty-object residue — a fire-then-undo replay must be byte-identical
      // to a state that never touched `resources` at all, and `resources` follows the SAME
      // "absent == pristine" convention its own keys do (`?? {}` at every read, so `undefined`
      // and `{}` already behave identically).
      const nextResources = Object.keys(next).length > 0 ? next : undefined;
      const current = { ...c.current, resources: nextResources };
      if (nextResources === undefined) delete current.resources;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, current },
        },
      };
    }
    case "resource_delta": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      const resources = c.current.resources ?? {};
      const cur = resources[step.key] ?? 0; // absent resource reads as 0.
      const val = Math.max(0, cur + step.delta); // floor at 0 (a consume past empty clamps).
      if (val === cur) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: {
            ...c,
            current: { ...c.current, resources: { ...resources, [step.key]: val } },
          },
        },
      };
    }
    case "move": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      // Set the combatant's anchor cell to the recorded destination. `move_token`
      // (the manual tier) already validated reachability when this was authored;
      // playback is rules-free and just folds the recorded position.
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, pos: step.to },
        },
      };
    }
    case "forced_move": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      // Rules-free fold: write the recorded destination onto `pos`. The map query
      // that *picked* this cell ran once at resolve time (the spatial answerer);
      // playback never re-queries the map. A `null` destination (the answerer
      // could not place it) is recorded faithfully and folds as a no-op position.
      if (step.to === null) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, pos: step.to },
        },
      };
    }
    case "move_budget_delta": {
      // The acting unit's move budget only; anyone else has none to grow (replay is total).
      const turn = state.activation.turn;
      if (turn.phase !== "acting" || turn.actor !== step.target) return state;
      return {
        ...state,
        activation: { ...state.activation, turn: { ...turn, budget: { ...turn.budget, move: turn.budget.move + step.delta } } },
      };
    }
    case "mine_armed_set": {
      const c = state.combatants[step.target];
      if (!c || c.mineProfile === undefined) return state; // replay is total; a gone/non-mine target is a no-op.
      if (c.mineProfile.armed === step.armed) return state;
      return {
        ...state,
        combatants: {
          ...state.combatants,
          [step.target]: { ...c, mineProfile: { ...c.mineProfile, armed: step.armed } },
        },
      };
    }
    case "concealed_set": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      if ((c.concealed ?? false) === step.concealed) return state;
      return {
        ...state,
        combatants: { ...state.combatants, [step.target]: { ...c, concealed: step.concealed } },
      };
    }
    case "disclosure_set": {
      // A successful Scan's reveal — writes through the SAME `withDisclosureLevel` mutation
      // the GM's manual `set_disclosure` reducer case calls (one field, two writers, no
      // duplicated spread).
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      // Compare the RAW stored field, never `disclosureOf(c)` (the defaulted reader):
      // this is a storage-level idempotence check ("would this write actually change the
      // persisted value"), not a display decision — `disclosureOf` folding `undefined` to
      // `"class"` would make the "already unset, writing unset again" no-op unreachable
      // (`disclosureOf` never returns `undefined` to compare against `step.disclosure`
      // `undefined`), causing a spurious rewrite that breaks reference-identity no-op checks
      // elsewhere in the state.
      if (c.disclosure === step.disclosure) return state;
      return {
        ...state,
        combatants: { ...state.combatants, [step.target]: withDisclosureLevel(c, step.disclosure) },
      };
    }
    case "features_revealed": {
      const c = state.combatants[step.target];
      if (!c) return state; // replay is total; a gone target is a silent no-op.
      const next = withRevealedFeatures(c, step.ids);
      if (sameIds(c.revealedFeatureIds, next.revealedFeatureIds)) return state;
      return { ...state, combatants: { ...state.combatants, [step.target]: next } };
    }
    case "spawn_object": {
      // Idempotent at playback (mirrors `status_add`): an already-present id is a no-op
      // rather than clobbering whatever's there (a redo replaying atop an unrelated add,
      // or a re-run over a not-yet-recalled deploy).
      if (state.combatants[step.combatant.id]) return state;
      return {
        ...state,
        combatants: { ...state.combatants, [step.combatant.id]: step.combatant },
      };
    }
    case "despawn_object": {
      // Idempotent: removing an already-absent id is a no-op — replay is total.
      if (!state.combatants[step.combatant.id]) return state;
      const combatants = { ...state.combatants };
      delete combatants[step.combatant.id];
      return { ...state, combatants };
    }
    case "area_add": {
      // Idempotent at playback (mirrors `spawn_object`): an already-present id is a
      // no-op rather than clobbering whatever's there.
      const areaEffects = state.areaEffects ?? {};
      if (areaEffects[step.area.id]) return state;
      return { ...state, areaEffects: { ...areaEffects, [step.area.id]: step.area } };
    }
    case "area_remove": {
      // Idempotent: removing an already-absent id is a no-op — replay is total.
      const areaEffects = state.areaEffects ?? {};
      if (!areaEffects[step.area.id]) return state;
      const next = { ...areaEffects };
      delete next[step.area.id];
      return { ...state, areaEffects: next };
    }
    case "to_hit":
    case "damage":
    case "save":
    case "table_check":
    case "contest":
    case "miss_chance":
    // `cost_waived` records a cost a replacement prevented: nothing was spent.
    case "cost_waived":
    case "ai_cascade_check":
    // `link` marks a causal boundary for the narrator and the director; it mutates nothing.
    case "link":
    // `intercept` witnesses the structure-loss interception window's d6 (or a decline). Its
    // consequences — the charge spend, the HP restore, the compensating structure point — ride as
    // separate grounded steps in the same list, so this one mutates nothing.
    case "intercept":
      // Record-only — witnesses a roll, mutates nothing.
      return state;
  }
}

/**
 * Play a whole resolved step list back onto state, rules-free, in order. This is
 * the function every non-authority participant runs (other clients, log replay,
 * redo). Folds each step through {@link applyResolvedStep}.
 */
export function applyResolved(
  state: EncounterState,
  steps: readonly ResolvedStep[],
): EncounterState {
  let cur = state;
  for (const step of steps) cur = applyResolvedStep(cur, step);
  return cur;
}

/** The track a delta step moves, read as `applyResolvedStep` reads it; `undefined` for any other step. */
function trackValue(state: EncounterState, step: ResolvedStep): number | undefined {
  const c = "target" in step && typeof step.target === "string" ? state.combatants[step.target] : undefined;
  if (!c) return undefined;
  switch (step.op) {
    case "hp_delta":
      return c.current.hp;
    case "heat_delta":
      return c.base.maxHeat === undefined ? undefined : (c.current.heat ?? 0);
    case "structure_delta":
      return c.current.structure ?? c.base.maxStructure;
    case "stress_delta":
      return c.current.stress ?? c.base.maxStress;
    default:
      return undefined;
  }
}

/**
 * {@link applyResolved}, also returning the steps as they actually landed: a track delta
 * (`hp_delta`, `heat_delta`, `structure_delta`, `stress_delta`) that the fold clamped is rewritten
 * to the change it made, and dropped when it changed nothing. The resolver owes effective deltas,
 * but one sized off stale state (BC-016: a rider read the pre-hit HP) clamps here silently, and
 * the naive inverse would then restore HP that was never lost. Inverting `landed` instead keeps
 * undo exact whatever the emitter recorded.
 */
export function applyResolvedLanded(
  state: EncounterState,
  steps: readonly ResolvedStep[],
): { state: EncounterState; landed: ResolvedStep[] } {
  let cur = state;
  const landed: ResolvedStep[] = [];
  for (const step of steps) {
    const before = trackValue(cur, step);
    const next = applyResolvedStep(cur, step);
    const after = trackValue(next, step);
    if (before === undefined || after === undefined || !("delta" in step) || after - before === step.delta) landed.push(step);
    else if (after !== before) landed.push({ ...step, delta: after - before });
    cur = next;
  }
  return { state: cur, landed };
}

/** Order-sensitive equality of two optional id lists, for the `features_revealed` no-op check. */
function sameIds(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.length === b.length && a.every((id, i) => id === b[i]);
}
